import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
} from "react";
import { COLLECT_READY, canSeeUniversity } from '../constants/featureFlags'
import { useSharedPhotosSupport } from '../hooks/useSharedPhotosSupport'
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { clearAuthHistory } from "../utils/platform";
import { HOME_SNAPSHOT_KEY } from "../utils/homeFeedCache";
import { useTutorialStore } from "../stores/tutorialStore";
import SearchBar from "../components/SearchBar";
import { useNotificationStore } from "../stores/notificationStore";
import Gap from "../components/Gap";
import { ReviewProps } from "../types/review";
import BottomNav from "../components/BottomNav";
import { BOTTOM_NAV_HEIGHT } from "../constants/layout";
import { HOME_NAV_ITEM } from "../types/navItem";
import { reviewApi } from "../api/review";
import LoadingSpinner from "../components/LoadingSpinner";
import { VisitType } from "../constants/visitType";
import TabFilter, { TabFilterOption } from "../components/TabFilter";
import FeedReviewBox from "../components/FeedReviewBox";
import { useAuthStore } from "../stores/authStore";
import { useLocationStore } from "../stores/locationStore";
import { isIntroSeen } from "./OnboardingIntro";

type FilterType = "recommended" | "following";

const FILTER_PARAM = "filter";

/**
 * 추천 피드를 자동으로 다시 불러오는 최소 간격.
 *
 * 서버는 추천 목록을 사용자별 스냅샷으로 캐시했다가 회차가 바뀔 때만 다시
 * 만든다. 그 전에 요청해봐야 똑같은 목록이 오고 스크롤만 위로 되감기므로,
 * 마지막으로 받아온 지 이만큼 지났을 때만 갱신한다.
 */
const RECOMMENDED_REFRESH_MS = 10 * 60 * 1000;
const RECOMMENDED_LOADED_AT_KEY = "home_recommended_loaded_at";

/**
 * 스크롤 위치는 탭마다 따로 기억한다.
 *
 * 하나의 키를 공유하면 팔로잉에서 스크롤을 내린 채 추천으로 넘어갔을 때
 * 추천도 같은 높이에서 시작한다. 추천은 방금 처음 여는 목록이므로 맨 위에서
 * 시작해야 하고, 팔로잉으로 돌아오면 보던 자리로 돌아가야 한다.
 */
const scrollKeyFor = (filter: FilterType) => `home_scroll_position:${filter}`;

/**
 * 피드 API 응답 한 건을 화면용 형태로 바꾼다.
 * 초기 로드·새로고침·미리 받기 세 곳이 같은 변환을 쓴다.
 */
const toFeedReview = (review: any): ReviewProps => ({
            id: review.id,
            placeId: review.placeId,
            placeAddress: review.placeAddress,
            user: {
              nickname: review.user.nickname,
              username: review.user.username ?? undefined,
              profileImage: review.user.profileImage || undefined,
            },
            restaurant: {
              name: review.restaurant.name,
              rating: review.restaurant.rating,
              category: review.restaurant.category,
            },
            reviewText: review.reviewText,
            images: review.images,
            imageBlurs: review.imageBlurs,
            likes: review.likes,
            isLiked: review.isLiked,
            comments: review.comments,
            date: review.date,
            dateDisplay: review.dateDisplay,
            visitType: review.visitType as VisitType,
            isPublic: review.isPublic,
            visitNumber: review.visitNumber,
            earlierVisits: review.earlierVisits,
            isFollowing: review.isFollowing,
            userId: review.user.userId || undefined,
            isBookmarked: review.isBookmarked || false,
            hasUserReview: review.hasUserReview || false,
            hideScore: review.hideScore || false,
            taggedFriends: review.taggedFriends?.map((f: any) => ({
              id: f.id,
              username: f.username,
              nickname: f.nickname,
              profileImage: f.profileImage || f.profile_image,
            })),
          });

/**
 * 홈 목록 스냅샷 키.
 *
 * 예전에는 home_reviews / home_filter / home_page / home_has_more / home_cursor를
 * 따로 저장했다. 각각 다른 시점에 갱신되니 어느 한쪽만 최신인 순간이 생기고,
 * 그때 복원하면 탭과 목록이 어긋난 화면이 떴다. 하나로 묶어 그 틈을 없앤다.
 */
// 키는 utils/homeFeedCache 에 둔다 — 리뷰를 쓴 뒤 그쪽에서 이 스냅샷을 버린다.

type HomeSnapshot = {
  filter: FilterType;
  reviews: ReviewProps[];
  page: number;
  hasMore: boolean;
  cursor: string | null;
};

/**
 * 이번 마운트에서 스냅샷을 실제로 쓸 것인지.
 *
 * 스냅샷이 '있다'와 '쓴다'는 다르다. URL이 다른 탭을 지정하면 저장된 목록은
 * 버리고 새로 받는다. 이 둘을 구분하지 않으면, 버릴 스냅샷을 보고 "로딩 아님"으로
 * 시작했다가 목록이 빈 채로 그려져 '아직 기록이 없습니다'가 잠깐 스친다.
 */
function willRestoreSnapshot(): boolean {
  const snapshot = readHomeSnapshot();
  if (!snapshot || snapshot.reviews.length === 0) {
    return false;
  }
  const urlFilter = new URLSearchParams(window.location.search).get(
    FILTER_PARAM,
  );
  const wanted =
    urlFilter === "recommended"
      ? "recommended"
      : urlFilter === "following"
        ? "following"
        : snapshot.filter;

  return snapshot.filter === wanted;
}

/** 저장해둔 홈 스냅샷을 읽는다. 형태가 깨졌으면 없는 것으로 친다. */
function readHomeSnapshot(): HomeSnapshot | null {
  const raw = sessionStorage.getItem(HOME_SNAPSHOT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as HomeSnapshot;
    if (!Array.isArray(parsed?.reviews)) return null;
    if (parsed.filter !== "following" && parsed.filter !== "recommended") {
      return null;
    }
    return {
      filter: parsed.filter,
      reviews: parsed.reviews,
      page: typeof parsed.page === "number" ? parsed.page : 1,
      hasMore: !!parsed.hasMore,
      cursor: parsed.cursor ?? null,
    };
  } catch {
    return null;
  }
}

const MainHome: React.FC = () => {
  const navigate = useNavigate();

  /* 사진 담아두기 입구를 그릴지 — 앱이 그 통로를 가진 빌드일 때만 'ready' */
  const collectSupported = useSharedPhotosSupport() === 'ready';
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loadNotifications, getUnreadCount } = useNotificationStore();
  const { isAuthenticated, userId } = useAuthStore();

  // 앱 웹뷰는 Splash(/)를 안 거치고 /main-home으로 바로 진입 → 온보딩 미열람이면 최초 1회 온보딩으로.
  useEffect(() => {
    if (!isIntroSeen()) {
      navigate("/onboarding-intro", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 알림 권한 요청은 App.tsx 로 옮겼다 (2026-10-18).
  // 여기 있으면 홈을 거치지 않는 진입에서는 영영 묻지 않았다.
  // → utils/fcmRegister.ts 의 requestNotificationPermissionOnce

  // 코치마크 튜토리얼: 환경설정 > 튜토리얼로 열었을 때만 표시한다.
  // 가입 후 최초 1회 자동으로 띄우던 동작은 제거했다(사용자가 요청하지 않은 화면을
  // 먼저 덮지 않는다). 실제 렌더는 AppRoutes에서 전역으로(홈→마이 탭 이어짐).
  const openTutorial = useTutorialStore((s) => s.open);
  useEffect(() => {
    if (!isAuthenticated) return;
    const replay = (location.state as { replayTutorial?: boolean } | null)
      ?.replayTutorial;
    if (!replay) return;
    // 재열람 요청: state 소비(뒤로가기/새로고침 시 반복 방지).
    // location.state는 history.state.usr에 들어가므로 그것만 비운다.
    // {}로 통째로 덮으면 react-router가 쓰는 idx·key까지 날아가 뒤로가기·
    // 스와이프 백 판별이 어긋난다.
    window.history.replaceState(
      { ...window.history.state, usr: null },
      "",
    );
    openTutorial();
  }, [isAuthenticated, location.state, openTutorial]);

  // 푸시 열람 보고는 usePushOpenReport(AppRoutes)로 옮겼다.
  // 홈에만 있으면 홈이 아닌 곳으로 보내는 푸시가 집계되지 않았다.

  // URL 쿼리에서 필터 복원 (뒤로가기 시 탭 유지, 기본값: 팔로잉)
  const activeFilter: FilterType =
    searchParams.get(FILTER_PARAM) === "recommended"
      ? "recommended"
      : "following";
  const [reviews, setReviews] = useState<ReviewProps[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  /**
   * 팔로잉 탭 이어받기 위치.
   *
   * page 기반 OFFSET은 1페이지를 본 뒤 팔로우한 사람이 글을 올리면 목록 전체가
   * 한 칸 밀려서, 2페이지 첫 줄에 1페이지 마지막 줄이 다시 나온다.
   * 서버가 준 커서를 그대로 돌려주면 "마지막으로 본 지점 다음"부터 받아온다.
   */
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  /**
   * 첫 렌더부터 로딩 상태로 시작한다.
   *
   * false로 시작하면 마운트 직후 한 프레임 동안 "데이터도 없고 로딩도 아닌"
   * 상태가 되어 "아직 리뷰가 없습니다"가 먼저 그려진다. 실제 로드는 그 다음
   * 이펙트에서 시작하므로, 사용자에게는 빈 계정이 잠깐 떴다가 내 피드로
   * 바뀌는 것처럼 보인다.
   * 복원할 세션 데이터가 있으면 그건 즉시 그려지므로 로딩이 필요 없다.
   */
  const [isLoading, setIsLoading] = useState(() => !willRestoreSnapshot());
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isLoadingRef = useRef(false);
  const hasMoreRef = useRef(false);
  const currentPageRef = useRef(1);
  const nextCursorRef = useRef<string | null>(null);
  const loadReviewsRef = useRef<
    | ((page: number, reset?: boolean, filter?: FilterType) => Promise<void>)
    | null
  >(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const loadMoreIoRef = useRef<IntersectionObserver | null>(null);
  const feedScrollRef = useRef<HTMLDivElement | null>(null);
  const pullStartY = useRef(0);
  const pullStartX = useRef(0);
  const pullMoveY = useRef(0);
  const isPulling = useRef(false);
  /**
   * 이번 터치가 어느 방향인지.
   *
   * 'none'       - 이 터치는 새로고침 대상이 아님(맨 위가 아니거나 예외 영역)
   * 'undecided'  - 아직 방향을 모름. 이 구간에서는 아무것도 막지 않는다.
   * 'vertical'   - 세로로 확정 → 당겨서 새로고침 진행
   * 'horizontal' - 가로로 확정 → 이번 제스처는 새로고침을 포기
   */
  const pullAxis = useRef<'none' | 'undecided' | 'vertical' | 'horizontal'>(
    'none'
  );
  const [pullDistance, setPullDistance] = useState(0);

  /**
   * 탭별로 마지막에 보던 목록을 들고 있는다.
   *
   * 탭을 바꿀 때마다 데이터를 버리고 다시 받아오면, 응답이 올 때까지
   * 화면이 비어 "내용 → 빈 화면 → 내용"으로 깜빡인다. 캐시가 있으면
   * 전환 즉시 그려서 깜빡임이 없고, 갱신은 뒤에서 조용히 한다.
   */
  /**
   * 가장 최근에 시작한 목록 요청의 번호.
   *
   * 탭을 빠르게 오가면 이전 탭의 요청이 아직 날아오는 중인데 화면은 새 탭이다.
   * 그 응답이 뒤늦게 도착해 setReviews를 하면 추천 탭에 팔로잉 글이 뜬다.
   * 응답을 반영하기 전에 "내가 마지막 요청이 맞는지" 확인해 걸러낸다.
   */
  const feedRequestIdRef = useRef(0);

  /**
   * 지금 화면에 떠 있는 목록이 '어느 탭에서 받아온 것'인지.
   *
   * reviews만 봐서는 출처를 알 수 없었다. 그래서 탭을 바꿀 때 화면의 목록을
   * 무조건 '지금 탭' 이름으로 캐시에 넣었고, 응답이 아직 안 온 사이에 탭을
   * 되누르면 팔로잉 목록이 추천 이름으로 저장됐다. 그 캐시는 스스로 낫지
   * 않아서(추천 탭은 10분 관문에 막혀 재조회를 안 한다) 계속 잘못 보였다.
   */
  const reviewsFilterRef = useRef<FilterType>("following");

  /**
   * 지금 보고 있는 탭. async 콜백 안에서 쓰려면 ref여야 한다.
   * activeFilter는 매 렌더 searchParams에서 다시 계산되는 값이라,
   * 요청을 시작할 때 잡아둔 클로저 안에서는 이미 낡은 값이다.
   */
  const activeFilterRef = useRef<FilterType>("following");

  const tabCacheRef = useRef<
    Partial<
      Record<
        FilterType,
        {
          reviews: ReviewProps[];
          page: number;
          hasMore: boolean;
          cursor: string | null;
        }
      >
    >
  >({});

  // 스크롤 복원 중 깜빡임 방지
  const [isRestoringScroll, setIsRestoringScroll] = useState(() =>
    willRestoreSnapshot(),
  );

  /**
   * 이번 마운트에서 첫 자동 갱신을 건너뛸지.
   *
   * 저장된 목록을 되살릴 마운트(= 뒤로가기로 돌아온 경우)에는 건너뛴다.
   * 보던 자리로 돌아가는 게 목적인데 목록을 갈아끼우면 그 자리가 사라진다.
   */
  const skipFirstAutoRefreshRef = useRef(willRestoreSnapshot());

  hasMoreRef.current = hasMore;
  currentPageRef.current = currentPage;
  nextCursorRef.current = nextCursor;

  /*
   * 페이지·더있음·커서를 바꿀 때는 ref 도 그 자리에서 같이 바꾼다.
   *
   * 예전에는 ref 를 렌더 때만 맞췄다(위 세 줄). 그러면 응답을 받고
   * '불러오는 중'을 푼 뒤 화면이 다시 그려지기 전 짧은 틈에 바닥 감지가
   * 한 번 더 걸리면, 옛 페이지 번호·커서로 방금 받은 쪽을 또 요청했다.
   * 서버 기록으로 2쪽 이후 요청의 16%가 이렇게 같은 쪽을 두 번 받고 있었다
   * (2026-10-17~31). 여기는 붙일 때 걸러서 화면엔 안 보였지만 요청이 헛돌았다.
   */
  const applyPaging = useCallback(
    (page: number, more: boolean, cursor: string | null) => {
      currentPageRef.current = page;
      hasMoreRef.current = more;
      nextCursorRef.current = cursor;
      setCurrentPage(page);
      setHasMore(more);
      setNextCursor(cursor);
    },
    [],
  );
  activeFilterRef.current = activeFilter;

  // 리뷰 로드 함수
  const loadReviews = useCallback(
    async (page: number, reset: boolean = false, filter?: FilterType) => {
      // 이미 로딩 중이면 중복 호출 방지
      if (isLoadingRef.current) {
        return;
      }

      const currentFilter = filter || activeFilter;

      /*
       * 추천 피드를 더 내려볼 때 위치 권한을 한 번 물어본다.
       *
       * 여기서 묻는 이유가 있다. 가입 직후 첫 화면(1페이지)은 관리자가 골라둔
       * 기록이라 위치가 필요 없다. 그런데 그 뒤부터는 위치가 없으면 서울 사람에게
       * 진주·대구 가게가 섞여 나온다.
       *
       * 들어오자마자 물으면 골라둔 첫 화면을 팝업이 덮는다. 한 페이지를 다 보고
       * 더 내려보는 사람은 이미 관심을 보인 것이므로, 그때 묻는 편이 덜 뜬금없고
       * 허용률도 높다.
       *
       * 이미 허용한 상태면 팝업 없이 조용히 좌표만 채워진다. 거부해도 예전과
       * 똑같이 동작한다 — 좌표가 없으면 서버가 근접도 항을 아예 안 쓴다.
       */
      if (page > 1 && currentFilter === "recommended") {
        try {
          if (localStorage.getItem("loc_perm_asked_v1") !== "1") {
            localStorage.setItem("loc_perm_asked_v1", "1");
            void useLocationStore.getState().requestLocationPermission();
          }
        } catch {
          /* 저장소를 못 써도 피드 로딩은 계속한다 */
        }
      }

      // 이 요청의 신분증. 응답이 왔을 때 아직 유효한지 판단하는 데 쓴다.
      const requestId = ++feedRequestIdRef.current;

      if (reset) {
        setIsLoading(true);
        setCurrentPage(1);
      } else {
        setIsLoadingMore(true);
      }

      isLoadingRef.current = true;

      try {
        // 추천 피드 위치 근접 보정 — 현재 위치를 호출 시점에 읽어 전달(없으면 서버가 중립 처리)
        const response = await reviewApi.getFeed(
          page,
          currentFilter,
          useLocationStore.getState().location,
          // 이어받기(1페이지가 아님)일 때만 커서를 보낸다. reset이면 처음부터 다시 받는다.
          !reset ? nextCursorRef.current : null,
        );

        // API 응답을 ReviewProps 형식으로 변환
        const convertedReviews: ReviewProps[] = response.reviews.map(
          (review) => ({
            id: review.id,
            placeId: review.placeId,
            placeAddress: review.placeAddress,
            user: {
              nickname: review.user.nickname,
              username: review.user.username ?? undefined,
              profileImage: review.user.profileImage || undefined,
            },
            restaurant: {
              name: review.restaurant.name,
              rating: review.restaurant.rating,
              category: review.restaurant.category,
            },
            reviewText: review.reviewText,
            images: review.images,
            imageBlurs: review.imageBlurs,
            likes: review.likes,
            isLiked: review.isLiked,
            comments: review.comments,
            date: review.date,
            dateDisplay: review.dateDisplay,
            visitType: review.visitType as VisitType,
            isPublic: review.isPublic,
            visitNumber: review.visitNumber,
            earlierVisits: review.earlierVisits,
            isFollowing: review.isFollowing,
            userId: review.user.userId || undefined,
            isBookmarked: review.isBookmarked || false,
            hasUserReview: review.hasUserReview || false,
            hideScore: review.hideScore || false,
            taggedFriends: review.taggedFriends?.map((f: any) => ({
              id: f.id,
              username: f.username,
              nickname: f.nickname,
              profileImage: f.profileImage || f.profile_image,
            })),
          }),
        );

        // 더 최신 요청이 시작됐으면 이 응답은 쓸모가 없다.
        if (feedRequestIdRef.current !== requestId) {
          return;
        }

        const nextCursorValue = response.next_cursor ?? null;

        /**
         * 그 사이 사용자가 다른 탭으로 옮겨갔나.
         *
         * 옮겨갔다면 화면은 건드리지 않는다. 하지만 받아온 목록은 버리지 않고
         * '자기 탭' 이름으로 캐시에 넣어둔다. 되돌아왔을 때 바로 뜬다.
         * (예전엔 통째로 버려서, 되누르면 처음부터 다시 받아야 했다)
         */
        const isOtherTab = currentFilter !== activeFilterRef.current;

        if (reset) {
          tabCacheRef.current[currentFilter] = {
            reviews: convertedReviews,
            page: response.current_page,
            hasMore: response.has_more,
            cursor: nextCursorValue,
          };
          if (!isOtherTab) {
            setReviews(convertedReviews);
            reviewsFilterRef.current = currentFilter;
          }
        } else {
          // 이미 목록에 있는 리뷰는 버린다.
          //
          // 서버가 커서로 정확히 잘라주지만, 새로고침이 이어받기와 겹치거나
          // 추천 탭 스냅샷이 교체되는 순간처럼 목록이 어긋날 수 있는 길이 남아 있다.
          // 마지막 방어선으로 여기서 한 번 더 걸러 같은 글이 두 번 보이지 않게 한다.
          if (!isOtherTab) {
            setReviews((prev) => {
              const seen = new Set(prev.map((r) => r.id));
              const fresh = convertedReviews.filter((r) => !seen.has(r.id));
              if (fresh.length === 0) {
                return prev;
              }
              return [...prev, ...fresh];
            });
            reviewsFilterRef.current = currentFilter;
          }
          // 붙인 뒤에도 캐시가 최신 목록을 들고 있어야 탭을 오갈 때 어긋나지 않는다.
          const cached = tabCacheRef.current[currentFilter];
          if (cached) {
            const seen = new Set(cached.reviews.map((r) => r.id));
            cached.reviews = [
              ...cached.reviews,
              ...convertedReviews.filter((r) => !seen.has(r.id)),
            ];
            cached.page = response.current_page;
            cached.hasMore = response.has_more;
            cached.cursor = nextCursorValue;
          }
        }

        // 페이지·커서는 화면에 붙인 목록의 것이어야 한다.
        // 다른 탭 응답의 커서를 넣으면 이어받기가 엉뚱한 데서 이어진다.
        if (!isOtherTab) {
          applyPaging(response.current_page, response.has_more, nextCursorValue);
        }
        if (currentFilter === "recommended") {
          sessionStorage.setItem(
            RECOMMENDED_LOADED_AT_KEY,
            String(Date.now()),
          );
        }
      } catch (error) {
        console.error("리뷰 목록 로드 실패:", error);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
        isLoadingRef.current = false;
      }

      // 하단에 머문 채로 붙인 경우 IO가 다시 안 뜰 수 있어 observe를 한 번 갱신
      if (!reset) {
        requestAnimationFrame(() => {
          const sentinel = loadMoreSentinelRef.current;
          const io = loadMoreIoRef.current;
          if (!sentinel || !io || !hasMoreRef.current || isLoadingRef.current) {
            return;
          }

          io.unobserve(sentinel);
          io.observe(sentinel);
        });
      }
    },
    [activeFilter, applyPaging],
  );

  loadReviewsRef.current = loadReviews;

  // 홈 진입 시엔 위치를 요청하지 않는다(위치 권한은 지도 탭에서만).
  // 추천 피드는 위치가 있으면(지도서 허용) 근접 보정에 쓰고, 없으면 서버가 중립 처리.

  /**
   * 피드를 1페이지부터 다시 불러온다.
   * @param silent true면 당겨서 새로고침 인디케이터를 띄우지 않는다(자동 갱신용).
   */
  const handleRefresh = useCallback(async (silent = false) => {
    if (!silent) setIsRefreshing(true);
    // isRefreshing일 때는 renderReviewList의 로딩 스피너를 보여주지 않기 위해 isLoading을 사용하지 않음
    isLoadingRef.current = true;
    // 새로고침도 목록을 통째로 갈아끼우므로 요청 번호를 올려야 한다.
    // 안 그러면 이미 날아간 이어받기 응답이 새 1페이지 뒤에 그대로 붙어
    // 목록이 어긋나거나 같은 글이 두 번 뜬다.
    const requestId = ++feedRequestIdRef.current;
    try {
      const currentFilter = activeFilter;
      const response = await reviewApi.getFeed(
        1,
        currentFilter,
        useLocationStore.getState().location,
        null,
        // 사용자가 직접 당겨서 새로고침한 경우에만 추천을 다음 묶음으로 넘긴다.
        // silent는 화면 재진입·탭 전환 같은 자동 갱신이라 넘기면 안 된다.
        !silent,
      );
      const convertedReviews: ReviewProps[] = response.reviews.map(
        (review) => ({
          id: review.id,
          placeId: review.placeId,
          placeAddress: review.placeAddress,
          user: {
            nickname: review.user.nickname,
            username: review.user.username ?? undefined,
            profileImage: review.user.profileImage || undefined,
          },
          restaurant: {
            name: review.restaurant.name,
            rating: review.restaurant.rating,
            category: review.restaurant.category,
          },
          reviewText: review.reviewText,
          images: review.images,
          imageBlurs: review.imageBlurs,
          likes: review.likes,
          isLiked: review.isLiked,
          comments: review.comments,
          date: review.date,
          dateDisplay: review.dateDisplay,
          visitType: review.visitType as VisitType,
          isPublic: review.isPublic,
          visitNumber: review.visitNumber,
          earlierVisits: review.earlierVisits,
          isFollowing: review.isFollowing,
          userId: review.user.userId || undefined,
          isBookmarked: review.isBookmarked || false,
          hasUserReview: review.hasUserReview || false,
          hideScore: review.hideScore || false,
          taggedFriends: review.taggedFriends?.map((f: any) => ({
            id: f.id,
            username: f.username,
            nickname: f.nickname,
            profileImage: f.profileImage || f.profile_image,
          })),
        }),
      );
      // 내가 마지막 요청이 아니면 화면을 건드리지 않는다.
      if (feedRequestIdRef.current !== requestId) {
        return;
      }

      const nextCursorValue = response.next_cursor ?? null;
      // 받아온 건 언제나 '자기 탭' 이름으로 남긴다.
      tabCacheRef.current[currentFilter] = {
        reviews: convertedReviews,
        page: response.current_page,
        hasMore: response.has_more,
        cursor: nextCursorValue,
      };
      // 화면은 지금 그 탭을 보고 있을 때만 갈아끼운다.
      if (currentFilter === activeFilterRef.current) {
        setReviews(convertedReviews);
        reviewsFilterRef.current = currentFilter;
        applyPaging(response.current_page, response.has_more, nextCursorValue);
      }
      if (currentFilter === "recommended") {
        sessionStorage.setItem(RECOMMENDED_LOADED_AT_KEY, String(Date.now()));
      }
    } catch (error) {
      console.error("새로고침 실패:", error);
    } finally {
      isLoadingRef.current = false;
      setIsRefreshing(false);
      // 자동 갱신이 초기 로드를 앞질러 가로챈 경우(loadReviews가 isLoadingRef
      // 때문에 조기 반환) 여기서 초기 로딩 표시를 반드시 내려야 한다.
      // 안 그러면 목록은 채워졌는데 스피너만 계속 돈다.
      setIsLoading(false);
    }
  }, [activeFilter, applyPaging]);

  /**
   * 화면에 들어올 때 피드를 자동으로 갱신한다.
   *  - 팔로잉: 항상. 아는 사람이 방금 올린 글이라 신선도가 전부다.
   *  - 추천: 마지막으로 받아온 지 10분이 지났을 때만. 서버가 스냅샷을 캐시하므로
   *    그 전에 다시 부르면 같은 목록만 오고 스크롤 위치만 잃는다.
   */
  const maybeAutoRefresh = useCallback(() => {
    // 첫 진입은 초기 로드가 이미 받아오는 중이라 중복 요청할 필요가 없다.
    if (isLoadingRef.current) return;

    // 뒤로가기로 돌아와 보던 목록을 되살리는 마운트라면 이번 한 번은 건너뛴다.
    //
    // 위 isLoadingRef 방어로는 못 막는다. 스냅샷 복원 경로는 loadReviews를
    // 부르지 않아 '받아오는 중'이 아니기 때문이다. 게다가 이 effect가 복원
    // effect보다 먼저 돈다(607행 vs 790행). 그래서 사진을 보고 돌아오면
    // 복원한 목록이 곧바로 새 목록으로 갈아끼워지고, 스크롤은 이미 '복원 완료'로
    // 표시돼 다시 잡히지 않아 엉뚱한 자리의 다른 글이 보였다.
    //
    // 추천 탭은 아래 10분 관문에 막혀 이 일이 드물었고, 관문이 없는 팔로잉
    // 탭에서만 늘 재현됐다.
    //
    // 깃발은 한 번만 쓴다. 이후의 탭 전환·앱 복귀 때는 평소대로 갱신한다.
    if (skipFirstAutoRefreshRef.current) {
      skipFirstAutoRefreshRef.current = false;
      return;
    }

    if (activeFilter === "recommended") {
      const lastLoadedAt = Number(
        sessionStorage.getItem(RECOMMENDED_LOADED_AT_KEY) || 0,
      );
      if (Date.now() - lastLoadedAt < RECOMMENDED_REFRESH_MS) return;
    }

    void handleRefresh(true);
  }, [activeFilter, handleRefresh]);

  // 화면 진입·탭 전환 시
  useEffect(() => {
    maybeAutoRefresh();
  }, [maybeAutoRefresh]);

  // 앱을 다시 켜거나 다른 탭에서 돌아온 경우
  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState !== "visible") return;
      maybeAutoRefresh();
    };
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () =>
      document.removeEventListener("visibilitychange", refreshWhenVisible);
  }, [maybeAutoRefresh]);

  // Pull-to-refresh 터치 핸들러
  const PULL_THRESHOLD = 80;

  /**
   * 방향을 확정하기까지 손가락이 움직여야 하는 거리(px).
   *
   * 이보다 작게 움직인 동안은 가로인지 세로인지 판단을 보류한다.
   * 너무 작으면 손떨림으로 오판하고, 너무 크면 당김 반응이 늦게 느껴진다.
   */
  const AXIS_LOCK_THRESHOLD = 8;

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    pullAxis.current = 'none';
    isPulling.current = false;

    // 모달/바텀시트(북마크 저장 등) 안에서 시작된 터치는 새로고침 제외
    if ((e.target as HTMLElement | null)?.closest?.("[data-no-pull-refresh]")) {
      return;
    }
    const scrollEl = feedScrollRef.current;
    if (!scrollEl || scrollEl.scrollTop > 0) return;

    pullStartY.current = e.touches[0].clientY;
    pullStartX.current = e.touches[0].clientX;
    // 여기서 바로 당김으로 확정하지 않는다.
    // 예전에는 맨 위이기만 하면 무조건 당김으로 봤는데, 그러면 사진을 좌우로
    // 넘기려는 손짓까지 세로 당김으로 잡혀 화면이 아래로 밀리고,
    // preventDefault 때문에 캐러셀의 가로 스크롤 자체가 취소됐다.
    pullAxis.current = 'undecided';
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (pullAxis.current === 'none' || pullAxis.current === 'horizontal') {
      return;
    }

    const scrollEl = feedScrollRef.current;
    if (!scrollEl || scrollEl.scrollTop > 0) {
      pullAxis.current = 'none';
      isPulling.current = false;
      setPullDistance(0);
      return;
    }

    const y = e.touches[0].clientY;
    const x = e.touches[0].clientX;

    if (pullAxis.current === 'undecided') {
      const dy = y - pullStartY.current;
      const dx = x - pullStartX.current;

      // 아직 충분히 움직이지 않았으면 판단을 미룬다.
      // 이 구간에서 아무것도 막지 않아야 브라우저가 가로 스크롤을 시작할 수 있다.
      if (
        Math.abs(dx) < AXIS_LOCK_THRESHOLD &&
        Math.abs(dy) < AXIS_LOCK_THRESHOLD
      ) {
        return;
      }

      // 가로가 더 크면 사진 넘기기다 — 이번 제스처는 새로고침을 포기한다
      if (Math.abs(dx) > Math.abs(dy)) {
        pullAxis.current = 'horizontal';
        return;
      }

      // 위로 올리는 건 피드를 더 보려는 것이므로 당김이 아니다
      if (dy <= 0) {
        pullAxis.current = 'none';
        return;
      }

      pullAxis.current = 'vertical';
      isPulling.current = true;
    }

    pullMoveY.current = y;
    const distance = Math.max(0, pullMoveY.current - pullStartY.current);
    // 감속 효과
    const dampened = Math.min(distance * 0.5, 120);
    setPullDistance(dampened);
  }, []);

  /**
   * 터치가 취소됐을 때(가로 스크롤이 시작되며 iOS가 터치를 가져가는 경우 등)
   * 새로고침 없이 상태만 되돌린다. 이걸 안 하면 화면이 밀린 채로 굳는다.
   */
  const handleTouchCancel = useCallback(() => {
    pullAxis.current = 'none';
    isPulling.current = false;
    setPullDistance(0);
  }, []);

  const handleTouchEnd = useCallback(() => {
    const wasPulling = isPulling.current;
    pullAxis.current = 'none';
    isPulling.current = false;
    if (!wasPulling) return;
    if (pullDistance >= PULL_THRESHOLD) {
      handleRefresh();
    }
    setPullDistance(0);
  }, [pullDistance, handleRefresh]);

  // iOS: 맨 위에서 "아래로 당길 때"만 네이티브 문서 바운스(러버밴드)를 막는다.
  // (React onTouchMove는 passive라 preventDefault가 안 먹으므로 non-passive 네이티브 리스너로.
  //  이걸 안 하면 iOS 웹뷰에서 문서 전체가 바운스되며 상단 고정 영역까지 같이 밀림.
  //  위로 스크롤(피드 더 보기)은 막지 않는다.)
  useEffect(() => {
    const el = feedScrollRef.current;
    if (!el) return;
    const onTouchMoveNative = (e: TouchEvent) => {
      // 세로로 확정된 뒤에만 막는다.
      // 'undecided' 상태에서 막으면 브라우저가 가로 스크롤을 시작하지 못해
      // 사진이 안 넘어간다. 방향이 정해질 때까지는 손대지 않는다.
      if (pullAxis.current !== 'vertical' || !isPulling.current) return;
      const y = e.touches[0]?.clientY ?? 0;
      if (y > pullStartY.current) e.preventDefault();
    };
    el.addEventListener("touchmove", onTouchMoveNative, { passive: false });
    return () => el.removeEventListener("touchmove", onTouchMoveNative);
  }, []);

  const attachLoadMoreSentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      loadMoreSentinelRef.current = node;

      if (loadMoreIoRef.current) {
        loadMoreIoRef.current.disconnect();
        loadMoreIoRef.current = null;
      }

      if (!node) {
        return;
      }

      const root = node.closest("[data-feed-scroll]");
      if (!root) {
        return;
      }

      const io = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry?.isIntersecting) {
            return;
          }

          if (isLoadingRef.current || !hasMoreRef.current) {
            return;
          }

          const fetchMore = loadReviewsRef.current;
          if (fetchMore) {
            void fetchMore(currentPageRef.current + 1, false);
          }
        },
        { root, threshold: 0 },
      );

      io.observe(node);
      loadMoreIoRef.current = io;
    },
    [],
  );

  // 최초 로드 여부 추적
  const isFirstLoadRef = useRef(true);

  // 초기 마운트 시 한 번만 실행 (URL의 filter 파라미터로 복원)
  useEffect(() => {
    clearAuthHistory();

    // 뒤로가기로 돌아온 경우 (이미 리뷰가 있으면) 스크롤만 복원
    const snapshot = readHomeSnapshot();

    // URL이 탭을 명시했으면 그게 우선이다.
    // (예: 온보딩 건너뛰기가 filter=recommended로 보내는 경우)
    const urlFilter = searchParams.get(FILTER_PARAM);
    const wantedFilter: FilterType =
      urlFilter === "recommended"
        ? "recommended"
        : urlFilter === "following"
          ? "following"
          : (snapshot?.filter ?? "following");

    // 저장해둔 목록은 '그 탭의 것일 때만' 쓴다.
    // 탭이 다르면 화면과 내용이 어긋나므로 버리고 새로 받는다.
    if (
      snapshot &&
      snapshot.filter === wantedFilter &&
      snapshot.reviews.length > 0 &&
      isFirstLoadRef.current
    ) {
      // 탭 표시를 먼저 맞춘 뒤 목록을 꽂는다 (중간에 어긋난 프레임이 없도록)
      if (wantedFilter === "recommended" && urlFilter !== "recommended") {
        setSearchParams({ [FILTER_PARAM]: "recommended" }, { replace: true });
      }
      setReviews(snapshot.reviews);
      reviewsFilterRef.current = wantedFilter;
      applyPaging(snapshot.page, snapshot.hasMore, snapshot.cursor);
      isFirstLoadRef.current = false;
      // 스크롤 복원은 아래 useLayoutEffect가 목록이 그려지는 즉시
      // (페인트 전에) 처리한다.

      loadNotifications();
      return;
    }

    isFirstLoadRef.current = false;
    setIsRestoringScroll(false);
    if (wantedFilter === "recommended" && urlFilter !== "recommended") {
      setSearchParams({ [FILTER_PARAM]: "recommended" }, { replace: true });
    }
    // setSearchParams는 다시 그려진 뒤에야 activeFilter에 반영된다.
    // 응답이 그보다 먼저 오면 '다른 탭 응답'으로 오인해 화면에 안 붙는다.
    // 요청을 보내기 전에 여기서 확정해둔다.
    activeFilterRef.current = wantedFilter;
    loadReviews(1, true, wantedFilter);
    loadNotifications();

    // FCM 토큰 등록은 App 레벨(전역)로 이동 (utils/fcmRegister) —
    // MainHome에 안 와도(온보딩 신규 유저) 인증되면 등록되도록.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (value: string) => {
    const filter = value as FilterType;
    if (filter === activeFilter) return;

    // 떠나는 탭의 현재 위치를 확정 저장한다(스크롤 이벤트가 마지막 값을
    // 못 남겼을 수 있다). 그래야 돌아왔을 때 보던 자리로 복귀한다.
    //
    // 단 이 탭의 복원이 아직 안 끝났다면 화면의 scrollTop은 0이다.
    // 그걸 저장하면 애써 기억해둔 위치를 0으로 덮어쓴다.
    // (탭을 빠르게 오갈 때 위치가 초기화되던 원인)
    if (feedScrollRef.current && restoredForFilterRef.current === activeFilter) {
      sessionStorage.setItem(
        scrollKeyFor(activeFilter),
        String(feedScrollRef.current.scrollTop),
      );
    }

    // 떠나는 탭의 목록을 캐시에 넣어둔다(돌아올 때 즉시 복원).
    //
    // 출처가 맞을 때만 넣는다. 탭을 누른 직후 응답이 아직 안 왔으면 화면의
    // 목록은 여전히 이전 탭 것이라, 그대로 저장하면 이름과 내용이 어긋난다.
    // 어긋난 캐시는 스스로 낫지 않으므로, 저장하지 않고 비워두는 편이 낫다
    // (다음에 그 탭을 누르면 새로 받아온다).
    if (reviews.length > 0 && reviewsFilterRef.current === activeFilter) {
      tabCacheRef.current[activeFilter] = {
        reviews,
        page: currentPage,
        hasMore,
        cursor: nextCursor,
      };
    }

    restoredForFilterRef.current = null;

    setSearchParams(filter === "following" ? {} : { [FILTER_PARAM]: filter }, {
      replace: true,
    });
    // 다시 그려지기 전에도 '지금 탭'을 알아야 응답을 옳게 가른다.
    activeFilterRef.current = filter;

    const cached = tabCacheRef.current[filter];
    if (cached) {
      // 캐시가 있으면 즉시 그린다 — 빈 화면을 거치지 않는다.
      //
      // 스크롤은 여기서 직접 넣으면 안 된다. setReviews는 비동기라 이 시점의
      // DOM에는 아직 이전 탭 목록이 들어 있고, 그 높이에 맞춰 scrollTop이
      // 잘려버린다(두 탭이 같이 움직이는 것처럼 보이던 원인).
      // 새 목록이 그려진 뒤 useLayoutEffect가 잡도록 맡긴다.
      setReviews(cached.reviews);
      reviewsFilterRef.current = filter;
      applyPaging(cached.page, cached.hasMore, cached.cursor ?? null);
      setIsLoading(false);
      setIsRestoringScroll(false);
      // 캐시가 있으면 그대로 보여주고 다시 받아오지 않는다.
      //
      // 예전에는 여기서 조용히 재조회를 걸었는데, 탭을 오갈 때마다 요청이 나가
      // 보던 목록이 발밑에서 바뀌었다. 추천 탭은 1페이지 요청이 '다음 묶음'으로
      // 넘기는 신호이기도 해서, 오가는 것만으로 안 본 기록이 건너뛰어졌다.
      // 최신 내용이 필요하면 사용자가 당겨서 새로고침한다.
      return;
    }

    // 캐시가 없는 첫 진입만 로딩을 보여준다.
    setIsRestoringScroll(true);
    loadReviews(1, true, filter);
  };

  /**
   * 복원한 목록이 그려지자마자, 화면에 칠하기 전에 위치를 잡는다.
   *
   * requestAnimationFrame으로 하면 브라우저가 이미 한 번 그린 뒤라 상단이
   * 한 프레임 노출된다. useLayoutEffect는 페인트 직전에 동기로 돌아 그 틈이 없다.
   */
  // 어느 탭 기준으로 복원을 마쳤는지. 탭이 바뀌면 다시 복원해야 한다.
  /**
   * 반대편 탭을 미리 받아둔다.
   *
   * 캐시가 없으면 첫 전환에서만 로딩이 보여 한 번 깜빡인다. 화면이 자리를
   * 잡은 뒤 조용히 한 번 받아두면 그 깜빡임도 없어진다.
   * 목록 상태를 건드리지 않고 캐시에만 넣으므로 현재 화면에는 영향이 없다.
   */
  const prefetchedRef = useRef(false);
  useEffect(() => {
    if (prefetchedRef.current) return;
    if (reviews.length === 0) return;

    const other: FilterType =
      activeFilter === "following" ? "recommended" : "following";
    if (tabCacheRef.current[other]) return;

    const timer = setTimeout(() => {
      // 깃발은 실제로 요청을 보낼 때 세운다.
      //
      // 예전엔 타이머를 걸기 전에 세웠다. deps에 reviews.length가 있어서
      // 1.2초 안에 목록 길이가 바뀌면(무한스크롤·새로고침) cleanup이 타이머를
      // 지우는데, 다시 실행될 땐 깃발에 막혀 되걸지 못했다. 미리 받기가
      // 조용히 사라져 첫 탭 전환에서 늘 로딩이 보였다.
      prefetchedRef.current = true;
      reviewApi
        .getFeed(1, other, useLocationStore.getState().location)
        .then((response) => {
          tabCacheRef.current[other] = {
            reviews: response.reviews.map(toFeedReview),
            page: response.current_page,
            hasMore: response.has_more,
            cursor: response.next_cursor ?? null,
          };
        })
        .catch(() => {
          // 미리 받기 실패는 조용히 넘어간다. 탭을 누르면 그때 받는다.
        });
    }, 1200);
    return () => clearTimeout(timer);
  }, [reviews.length, activeFilter]);

  const restoredForFilterRef = useRef<FilterType | null>(null);
  useLayoutEffect(() => {
    if (restoredForFilterRef.current === activeFilter) return;
    if (reviews.length === 0) return;

    restoredForFilterRef.current = activeFilter;
    const savedScroll = sessionStorage.getItem(scrollKeyFor(activeFilter));
    if (feedScrollRef.current) {
      // 저장된 위치가 없으면 맨 위. 이전 탭의 스크롤이 남아 있으면 안 된다.
      feedScrollRef.current.scrollTop = savedScroll
        ? parseInt(savedScroll, 10)
        : 0;
    }
    setIsRestoringScroll(false);
  }, [reviews.length, activeFilter]);

  // 스크롤 위치 저장
  useEffect(() => {
    const scrollEl = feedScrollRef.current;
    if (!scrollEl) return;
    const handleScroll = () => {
      // 복원 전 스크롤(0)이 저장된 위치를 덮어쓰지 않게 한다.
      if (restoredForFilterRef.current !== activeFilter) return;
      sessionStorage.setItem(
        scrollKeyFor(activeFilter),
        String(scrollEl.scrollTop),
      );
    };
    scrollEl.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollEl.removeEventListener("scroll", handleScroll);
  }, [isLoading, activeFilter]);

  /**
   * 뒤로가기 복원용으로 목록을 저장한다.
   *
   * 목록과 탭을 한 덩어리로 저장하는 이유
   * ----------------------------------
   * 예전에는 home_reviews / home_filter를 따로 넣었다. 둘은 각각 쓰이므로
   * 어느 한쪽만 갱신된 순간이 생기고, 그때 복원하면 '추천 탭인데 팔로잉 목록'
   * 같은 짝이 어긋난 화면이 떴다. 한 키에 묶으면 그런 중간 상태가 없다.
   *
   * 다만 한 키에 묶는 것만으로는 부족했다. reviews와 activeFilter는 서로 다른
   * 시점에 바뀐다. 탭을 누르면 activeFilter가 먼저 바뀌고 이 저장이 즉시 돌아,
   * 응답이 오기도 전에 '이전 탭 목록'을 '새 탭 이름'으로 남겼다. 그 상태로
   * 사진을 눌러 나갔다 돌아오면 짝이 어긋난 채 복원됐다.
   *
   * 그래서 화면의 목록이 정말 이 탭 것일 때만 저장한다. 아니면 건너뛴다 —
   * 응답이 도착해 목록이 갱신되면 그때 다시 이 effect가 돌아 제대로 남는다.
   */
  useEffect(() => {
    if (reviews.length > 0 && reviewsFilterRef.current === activeFilter) {
      sessionStorage.setItem(
        HOME_SNAPSHOT_KEY,
        JSON.stringify({
          filter: activeFilter,
          reviews,
          page: currentPage,
          hasMore,
          cursor: nextCursor ?? null,
        }),
      );
    }
  }, [reviews, activeFilter, currentPage, hasMore, nextCursor]);

  const renderAppBar = () => {
    const unreadCount = getUnreadCount();

    return (
      <div className="w-full flex items-center">
        <div className="app-container flex items-center justify-between px-20px py-14px">
          <button
            type="button"
            data-tutorial="logo"
            onClick={() =>
              navigate("/onboarding-rank", { state: { fromHome: true } })
            }
            className="shrink-0 flex items-center h-28px"
            aria-label="지도 리스트로 기록 시작"
          >
            <span
              className="font-pretendard font-bold text-22px leading-none text-brandPrimary"
              style={{
                letterSpacing: "0.2em",
                paddingLeft: "0.2em",
                transform: "translateY(1px)",
              }}
            >
              TASTEE
            </span>
          </button>

          <div className="flex items-center gap-16px">
          {/*
            대학교 연결 — 아직 도연(17)·테이스티(455) 계정에만 보인다.
            자격은 constants/featureFlags.ts 한 곳에서만 정한다.
          */}
          {canSeeUniversity(userId) && (
            <button
              type="button"
              onClick={() => navigate("/university")}
              className="w-28px h-28px"
              aria-label="대학교"
            >
              <img
                src="/images/university-icon-v4.png"
                alt=""
                className="object-contain"
                style={{ width: "28px", height: "28px" }}
              />
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate("/notification-list")}
            className="relative w-28px h-28px"
          >
            <img
              src="/images/bell-icon.png"
              alt="알림"
              className="object-contain"
              style={{ width: "28px", height: "28px" }}
            />

            {unreadCount > 0 && (
              <div className="absolute top-[-2px] right-[-2px] bg-semanticError rounded-full w-10px h-10px" />
            )}
          </button>
          </div>
        </div>
      </div>
    );
  };

  const renderSearchBar = () => {
    return (
      <div className="w-full mt-8px px-20px">
        <SearchBar
          placeholder="검색"
          dataTutorial="search"
          onClick={() => navigate("/search")}
          height={44}
        />
      </div>
    );
  };

  const filterOptions: TabFilterOption[] = [
    { value: "recommended", label: "추천" },
    { value: "following", label: "팔로잉" },
  ];

  const renderBottomNav = () => {
    return (
      <>
        <div
          className="w-full"
          style={{ height: `${BOTTOM_NAV_HEIGHT}px` }}
        ></div>
        <BottomNav activePath={HOME_NAV_ITEM.path} />
      </>
    );
  };

  const renderReviewList = () => {
    // 목록이 이미 있으면 로딩 플래그와 무관하게 목록을 그린다.
    // 어느 경로에서든 플래그를 못 내려도 빈 스피너로 갇히지 않는다.
    if (isLoading && reviews.length === 0) {
      return (
        /*
          첫 진입 로딩(목록이 아직 하나도 없을 때)은 빈 문구와 **같은 자리**에 둔다.
          예전엔 py-40px 라 제 내용만 한 높이였고, 그래서 칸 맨 위에 앉았다.
          로딩이 끝나 "아직 기록이 없습니다"가 뜨면 글자가 가운데로 뚝 떨어졌다.

          감싼 칸(feedScrollRef)은 flex 컨테이너가 아니라 block 이라 flex-1 이
          안 먹는다. 높이는 정해져 있으므로 h-full 로 채운다. 빈 문구는
          absolute inset-0 으로 같은 칸을 덮으므로 둘의 중심이 같아진다.

          당겨서 새로고침(1278행)과 더보기(1223행)는 그대로 둔다 — 그쪽은
          위·아래가 제자리다.
        */
        <div className="w-full h-full flex items-center justify-center empty-state-center">
          <LoadingSpinner size={28} borderWidth={4} />
        </div>
      );
    }

    if (reviews.length === 0) {
      // 빈 상태 문구는 content 전체영역 기준 정중앙으로 띄우기 위해
      // 아래 absolute 오버레이(return 문)에서 렌더한다. 여기선 아무것도 안 그림.
      return null;
    }

    return (
      <div className="w-full flex flex-col">
        {reviews.map((review) => (
          <FeedReviewBox
            key={review.id}
            review={review}
            hideScore={review.hideScore || false}
          />
        ))}

        {hasMore && (
          <>
            <Gap height={24} />

            <div
              ref={attachLoadMoreSentinelRef}
              className={`
                w-full
                min-h-80px
                flex
                flex-col
                items-center
                justify-center
                py-24px
              `}
            >
              {isLoadingMore && <LoadingSpinner size={28} borderWidth={4} />}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <div className="app-container flex-1 flex flex-col overflow-hidden relative">
        {/* 앱바 */}
        {renderAppBar()}

        {/* 검색바 */}
        {renderSearchBar()}

        {/* 필터 버튼 */}
        <TabFilter
          options={filterOptions}
          activeValue={activeFilter}
          onChange={handleFilterChange}
        />

        <Gap height={8} />

        {/* 리뷰 리스트 */}
        <div
          ref={feedScrollRef}
          data-feed-scroll
          className={`
            flex-1
            overflow-y-auto
            scrollbar-hide
            relative
          `}
          style={{
            visibility: isRestoringScroll ? "hidden" : "visible",
            overscrollBehavior: "none",
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchCancel}
        >
          {/* Pull-to-refresh 인디케이터 */}
          {(pullDistance > 0 || isRefreshing) && (
            <div
              className="w-full flex items-center justify-center"
              style={{
                height: isRefreshing ? 60 : pullDistance,
                transition: isPulling.current ? "none" : "height 0.2s ease",
              }}
            >
              {isRefreshing ? (
                <LoadingSpinner size={28} borderWidth={3} />
              ) : (
                <div
                  className="text-textColorText3 text-12px font-pretendard"
                  style={{
                    opacity: Math.min(pullDistance / PULL_THRESHOLD, 1),
                  }}
                >
                  ↓
                </div>
              )}
            </div>
          )}
          {/*
            먹은 사진 담아두기로 들어가는 길.

            전에는 종 아이콘 옆 **+ 아이콘**이었다. 거기서는 무엇으로
            가는 길인지 아이콘만 보고 알 수가 없었다. 팔로잉 피드 맨 위에
            카드로 두면 하는 일이 글로 적힌다.

            왜 입구가 필요한가
            -----------------
            이게 없으면 들어갈 방법이 공유뿐인데, 공유는 **이미 늦다** —
            안드로이드는 앱이 깨어나기도 전에 사진을 복사해 버려서 그때
            위치 권한을 물어야 소용이 없다. 공유하기 전에 한 번이라도
            들어와 본 사람만 제대로 동작한다.

            왜 앱 버전을 보나
            ----------------
            이 통로는 1.0.43 에서 생겼다. 웹은 전원에게 즉시 가지만 앱은
            심사를 기다리므로, 켠 날부터 통과하는 날까지 **옛 앱 사용자**가
            이 카드를 보게 된다. 그 사람들이 누르면 /collect 가 앱의 대답을
            기다리다 포기하고 막다른 화면을 보여준다(실측: 5초 스피너).
            없는 기능은 보이지 않는 편이 낫다.

            두 탭 모두에 둔다. 추천으로 들어온 사람도 기록은 남긴다.

            여백은 피드와 같은 자를 쓴다 — 좌우 20 은 로고·프로필·날짜가
            쓰는 그 선이고(실측 20.0~369.5), 위아래 16 은 기록 한 칸이
            제 안에서 쓰는 간격(FeedReviewBox 의 첫 Gap 16)과 같다.
          */}
          {COLLECT_READY && collectSupported && (
            <button
              type="button"
              onClick={() => navigate("/collect")}
              className="w-full px-20px pt-16px pb-16px text-left"
            >
              {/*
                위아래 여백이 다른 건 한글 줄상자 때문이다. 글자 **잉크**를
                기준으로 시안(위 18 / 아래 25)에 맞추면 padding 은 14 / 19 가
                된다. 같은 값으로 두면 글이 위로 쏠려 보인다.
              */}
              <div className="w-full rounded-20px bg-white px-20px pt-14px pb-19px shadow-[0_2px_12px_rgba(75,54,33,0.06)]">
                <p className="text-style-labelL text-brown-80%-main">
                  먹은 사진 한 번에 기록하기
                </p>
                <p className="mt-3px text-style-body2 text-brown-80%-main">
                  이번 주 기록 잊기 전에 남겨볼까요?
                </p>
              </div>
            </button>
          )}

          {renderReviewList()}

          {/* 빈 상태: 비어 있는 칸의 광학 중앙(index.css의 .empty-state-center) */}
          {!isLoading && reviews.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none empty-state-center">
              <p className="text-style-body1 text-textColorText3">
                아직 기록이 없습니다
              </p>
            </div>
          )}
        </div>
      </div>

      {/* 바텀 네비 */}
      {renderBottomNav()}
    </div>
  );
};

export default MainHome;
